import React, { useCallback, useState, useEffect } from "react";
import { Slider, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useField, useFormikContext } from "formik";
import { QUESTION_TYPE_RADIUS } from "@/config/constants";

const marks = [
  {
    value: QUESTION_TYPE_RADIUS.MIN,
    label: `${QUESTION_TYPE_RADIUS.MIN} mile${QUESTION_TYPE_RADIUS.MIN > 1 ? "s" : ""}`,
  },
  {
    value: QUESTION_TYPE_RADIUS.MAX,
    label: `${QUESTION_TYPE_RADIUS.MAX} miles`,
  },
];

const RpCustomSlider = styled(Slider)(({ theme }) => ({
  color: (theme.vars || theme).palette.primary.main,
  height: 8,
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: (theme.vars || theme).palette.primary.main,
    border: "2px solid var(--neutral-white) !important",
    "&:focus, &:hover, &.Mui-active": {
      boxShadow: "inherit",
    },
  },
  "& .MuiSlider-track": {
    height: 12,
    borderRadius: "var(--radius-round)",
  },
  "& .MuiSlider-rail": {
    backgroundColor: "var(--neutral-500)",
    opacity: 1,
    height: 12,
    borderRadius: "var(--radius-round)",
  },
  "& .MuiSlider-mark": {
    background: "transparent",
  },
  "& .MuiSlider-markLabel": {
    color:
      (theme.vars || theme)?.palette?.neutral?.[500] || "var(--neutral-500)",
    fontSize: 10,
    fontWeight: 700,
    top: -10,
  },
  "& .MuiSlider-valueLabel": {
    backgroundColor: "transparent",
    color: (theme.vars || theme).palette.primary.main,
    fontSize: "16px",
    fontWeight: "bold",
    top: "50px",
    transition: "none !important",
    "&::before": {
      display: "none",
    },
  },
}));

function RpMuiFromikSlider({ name, ...props }) {
  const { setFieldValue } = useFormikContext();
  const [field] = useField(name);

  // Normalize initial value
  const getNormalizedValue = useCallback((val) => {
    const num = Number(val);
    if (isNaN(num)) return QUESTION_TYPE_RADIUS.MIN;
    if (num > QUESTION_TYPE_RADIUS.MAX) return QUESTION_TYPE_RADIUS.MAX;
    if (num < QUESTION_TYPE_RADIUS.MIN) return QUESTION_TYPE_RADIUS.MIN;
    return num;
  }, []);

  // Local state to handle slider movement smoothly without triggering parent re-renders continuously
  const [localValue, setLocalValue] = useState(() =>
    getNormalizedValue(field.value)
  );

  // Sync local state when Formik field value changes externally (e.g. form reset or initial load)
  useEffect(() => {
    setLocalValue(getNormalizedValue(field.value));
  }, [field.value, getNormalizedValue]);

  const handleChange = useCallback((_event, newValue) => {
    setLocalValue(newValue);
  }, []);

  const handleChangeCommitted = useCallback(
    (_event, newValue) => {
      setFieldValue(name, newValue);
    },
    [name, setFieldValue]
  );

  // Custom value label formatter
  const valueLabelFormat = useCallback((value) => {
    return `${value} MILE${Number(value) > 1 ? "S" : ""}`;
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <RpCustomSlider
        aria-label="Rp slider"
        value={localValue}
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
        name={name}
        min={QUESTION_TYPE_RADIUS.MIN}
        max={QUESTION_TYPE_RADIUS.MAX}
        marks={marks}
        valueLabelDisplay="on"
        valueLabelFormat={valueLabelFormat}
        {...props}
      />
    </Box>
  );
}

export default RpMuiFromikSlider;
